import CloseIcon from '@mui/icons-material/Close';
import GradientIcon from '@mui/icons-material/Gradient';
import SquareIcon from '@mui/icons-material/Square';
import { Box, Button, CircularProgress, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Modal, Stack, Typography } from '@mui/material';
import * as React from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { IGetMaterialPreviewResponse } from '../../api/material/iApiResponse';
import * as MaterialHelper from '../../api/material/materialHelper';
import { physicalData } from '../../conversion/physicalData';
import InputText from '../../shared/component/InputText';
import { EUnitOfMeasure } from '../../shared/enum';
import { IMaterial } from '../../shared/iProject';
import { useAppSelector } from '../../store/hook';
import { selectProject } from '../../store/project/projectSlice';
import MaterialDetail from './MaterialDetail';
import { selectUser } from '../../store/user/userSlice';

interface IMaterialProps {}

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
};

interface IBaseMaterialContext {
    baseMaterial: IMaterial;
    handleClose: () => void;
}

export const Context = React.createContext<IBaseMaterialContext | null>(null);

const Material = (props: IMaterialProps) => {
    const [isLoadingMaterialPreview, setIsLoadingMaterialPreview] = React.useState(false);
    const [materialPreview, setMaterialPreview] = React.useState<IGetMaterialPreviewResponse[]>();
    const [isLoadingMaterial, setIsLoadingMaterial] = React.useState(false);
    const [baseMaterial, setBaseMaterial] = React.useState<IMaterial>();
    const [open, setOpen] = React.useState(false);

    const [selectedIndex, setSelectedIndex] = React.useState<number>();

    const handleOpen = () => {
        let baseMaterialId = project.data!.material!.baseMaterialId ?? project.data?.material?.id;
        fetchBaseMaterial(baseMaterialId!);
        let initialIndex = materialPreview!.findIndex(x => x.id == baseMaterialId);
        setSelectedIndex(initialIndex);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const { t } = useTranslation();

    const project = useAppSelector(selectProject);
    const user = useAppSelector(selectUser);

    React.useEffect(() => {
        const fetchMaterialPreview = async () => {
            setIsLoadingMaterialPreview(true);
            let res = await MaterialHelper.getMaterialPreviewAsync(undefined, undefined, undefined, user.data!.isAdmin);
            setMaterialPreview(res.data!.value);
            setIsLoadingMaterialPreview(false);
        };
        fetchMaterialPreview();
    }, []);

    const fetchBaseMaterial = async (id: number) => {
        setIsLoadingMaterial(true);
        let res = await MaterialHelper.getMaterialByIdAsync(id);
        setBaseMaterial(res.data!);
        setIsLoadingMaterial(false);
    };

    const handleListItemClick = (index: number, materialId: number) => {
        setSelectedIndex(index);
        fetchBaseMaterial(materialId);
    };

    return (
        <Stack paddingTop={'1rem'}>
            <Button startIcon={<GradientIcon />} onClick={handleOpen}>
                {t('chooseMaterial')}
            </Button>
            <Modal open={open} onClose={handleClose} aria-labelledby='modal-modal-title' aria-describedby='modal-modal-description'>
                <Box mx={style}>
                    <Draggable cancel='.btn'>
                        <Stack variant='modal' direction={'column'} spacing={1} padding={'1rem'}>
                            <Stack variant='bottomLine' direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <Typography id='modal-material-title' variant='h6' component='h2'>
                                    {t('material')}
                                </Typography>
                                <IconButton aria-label='close-modal' onClick={handleClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                            {isLoadingMaterialPreview && <CircularProgress />}
                            {!isLoadingMaterialPreview && materialPreview?.length! > 0 && (
                                <>
                                    <Stack variant='bottomLineGreen' direction={'row'} justifyContent={'flex-end'} spacing={2}>
                                        <InputText disabled value={project.data?.material?.translations![0].name} inputLabel={t('currentMaterial')} />
                                        <InputText isNumber disabled value={project.data?.material?.physicalData[0].shieldsParameter} inputLabel={t('shieldsParameter')} />
                                        <InputText
                                            isNumber
                                            disabled
                                            customValue={
                                                project.data!.project!.unitOfMeasure === EUnitOfMeasure.Metric
                                                    ? project.data?.material?.physicalData[0].thickness
                                                    : project.data?.material?.physicalData[0].thicknessIn
                                            }
                                            inputLabel={t('thickness', {
                                                unitOfMeasure: physicalData['thickness'].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                            })}
                                        />
                                    </Stack>
                                    <Stack direction={'row'} spacing={2} flexGrow={1}>
                                        <Stack position={'relative'} width={'40%'}>
                                            <Stack variant='scrollable'>
                                                <List>
                                                    {materialPreview!.map((x, i) => (
                                                        <ListItemButton className='btn' selected={selectedIndex === i} onClick={e => handleListItemClick(i, x.id)}>
                                                            <ListItemText primary={x.name} />
                                                            <ListItemIcon style={{ justifyContent: 'flex-end' }}>
                                                                <SquareIcon style={{ fill: x.color }} />
                                                            </ListItemIcon>
                                                        </ListItemButton>
                                                    ))}
                                                </List>
                                            </Stack>
                                        </Stack>
                                        <Stack flexGrow={1} width={'60%'}>
                                            {isLoadingMaterial && <CircularProgress />}
                                            {!isLoadingMaterial && baseMaterial && (
                                                <Context.Provider value={{ baseMaterial, handleClose }}>
                                                    <MaterialDetail />
                                                </Context.Provider>
                                            )}
                                        </Stack>
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Draggable>
                </Box>
            </Modal>
        </Stack>
    );
};

export default Material;
